import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog'
import { Instagram, Facebook, Youtube, Clock, Trash2, Pencil, CheckCircle, AlertTriangle, Timer, ExternalLink, Info, XIcon, Eye, Play } from 'lucide-react'
import { useDeletePost } from '@/api/hooks/usePoster'
import { toast } from 'sonner'
import type { ScheduledPost } from '@/types'

function StatusBadge({ post, onShowError }: { post: ScheduledPost; onShowError?: () => void }) {
  const status = post.status || (post.published ? 'published' : 'pending')
  if (status === 'published') {
    return (
      <Badge className="gap-1 text-[10px] rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-none font-semibold">
        <CheckCircle className="h-2.5 w-2.5" /> Publicado
      </Badge>
    )
  }
  if (status === 'failed') {
    return (
      <Badge
        variant="destructive"
        className="gap-1 text-[10px] rounded-md font-semibold cursor-pointer hover:opacity-80 transition-opacity"
        onClick={onShowError}
      >
        <AlertTriangle className="h-2.5 w-2.5" /> Falhou
        {post.errorMessage && <Info className="h-2.5 w-2.5 ml-0.5" />}
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="gap-1 border-yellow-500/25 bg-yellow-500/10 text-[10px] text-yellow-600 dark:text-yellow-400 rounded-md font-semibold">
      <Timer className="h-2.5 w-2.5" /> Agendado
    </Badge>
  )
}

interface PostCardProps {
  post: ScheduledPost
  onEdit?: (post: ScheduledPost) => void
  compact?: boolean
}

export function PostCard({ post, onEdit, compact }: PostCardProps) {
  const deletePost = useDeletePost()
  const [showError, setShowError] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const canEdit = !post.published
  const isVideo = post.mediaType === 'video'
  const isStory = post.postType === 'story'
  const isFailed = post.status === 'failed'

  const handleDelete = () => {
    if (!confirm('Excluir post?')) return
    deletePost.mutate(post.id, {
      onSuccess: () => toast.success('Post excluido!'),
    })
  }

  return (
    <>
    <div
      className={`group rounded-xl border bg-card/60 backdrop-blur-sm p-3 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-pointer ${
        isFailed
          ? 'border-destructive/30 hover:border-destructive/40'
          : 'border-border/40 hover:border-primary/25'
      }`}
      onClick={() => setShowPreview(true)}
    >
      {/* Top row */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className="gap-1 text-[10px] rounded-md border-border/40">
            {post.platform === 'yt' ? (
              <Youtube className="h-2.5 w-2.5 text-red-500" />
            ) : post.platform === 'ig' ? (
              <Instagram className="h-2.5 w-2.5 text-purple-600 dark:text-purple-400" />
            ) : (
              <Facebook className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" />
            )}
            {post.platform === 'yt' ? 'YT' : post.platform === 'ig' ? 'IG' : 'FB'}
          </Badge>
          {isStory ? (
            <Badge className="gap-1 border-purple-500/25 bg-purple-500/10 text-[10px] text-purple-600 dark:text-purple-400 rounded-md" variant="outline">
              Story
            </Badge>
          ) : isVideo ? (
            <Badge className="gap-1 border-orange-500/25 bg-orange-500/10 text-[10px] text-orange-600 dark:text-orange-400 rounded-md" variant="outline">
              Reel
            </Badge>
          ) : (
            <Badge className="gap-1 border-blue-500/25 bg-blue-500/10 text-[10px] text-blue-600 dark:text-blue-400 rounded-md" variant="outline">
              Imagem
            </Badge>
          )}
          {post.accountName && (() => {
            const profileUrl = post.platform === 'yt' ? null
              : post.platform === 'ig' && post.accountName.startsWith('@')
                ? `https://instagram.com/${post.accountName.slice(1)}`
                : post.platform === 'fb' && post.pageId
                  ? `https://facebook.com/${post.pageId}`
                  : null
            return profileUrl ? (
              <a
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] font-medium text-muted-foreground transition-colors hover:text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {post.accountName}
              </a>
            ) : (
              <span className="text-[11px] font-medium text-muted-foreground">{post.accountName}</span>
            )
          })()}
        </div>
        <div className="flex items-center gap-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          {canEdit && onEdit && (
            <Button variant="ghost" size="icon" className="h-6 w-6 rounded-lg text-muted-foreground hover:text-foreground transition-colors" onClick={(e) => { e.stopPropagation(); onEdit(post) }}>
              <Pencil className="h-3 w-3" />
            </Button>
          )}
          {canEdit && (
            <Button variant="ghost" size="icon" className="h-6 w-6 rounded-lg text-muted-foreground hover:text-destructive transition-colors" onClick={(e) => { e.stopPropagation(); handleDelete() }}>
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Title + Message */}
      {post.title && (
        <p className="mb-1 text-xs font-semibold text-foreground truncate">{post.title}</p>
      )}
      <p className="mb-2 text-xs leading-relaxed text-foreground/90 line-clamp-2">{post.message}</p>

      {/* Error banner */}
      {isFailed && showError && post.errorMessage && (
        <div className="mb-2 rounded-lg border border-destructive/20 bg-destructive/5 px-2.5 py-2">
          <p className="text-[11px] font-medium text-destructive">Retorno da Meta:</p>
          <p className="text-[11px] text-destructive/80 leading-relaxed mt-0.5">{post.errorMessage}</p>
        </div>
      )}

      {/* Media thumbnail */}
      {post.imageUrl && !compact && (
        <div className="mb-2 overflow-hidden rounded-lg relative">
          {isVideo ? (
            <>
              <video src={post.imageUrl} className="h-16 w-full object-cover" muted />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <Play className="h-5 w-5 text-white drop-shadow-md" />
              </div>
            </>
          ) : (
            <img src={post.imageUrl} alt="" className="h-16 w-full object-cover" />
          )}
        </div>
      )}

      {/* Bottom row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground tabular-nums">
          <Clock className="h-2.5 w-2.5" />
          {new Date(post.scheduledAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className="flex items-center gap-1.5">
          {post.publishedUrl && (
            <a
              href={post.publishedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-0.5 rounded-md border border-primary/25 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary transition-colors hover:bg-primary/20"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-2.5 w-2.5" /> Ver post
            </a>
          )}
          {post.category && (
            <Badge variant="outline" className="px-1.5 text-[10px] rounded-md border-border/40">{post.category}</Badge>
          )}
          <StatusBadge post={post} onShowError={() => setShowError(v => !v)} />
        </div>
      </div>
    </div>

    {/* Preview Dialog */}
    <Dialog open={showPreview} onOpenChange={setShowPreview}>
      <DialogContent
        className="max-w-lg rounded-2xl border-border/40 p-0 overflow-hidden"
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="flex items-center justify-between border-b border-border/40 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-primary" />
            <span className="text-sm font-bold">Preview</span>
            <Badge variant="outline" className="gap-1 text-[10px] rounded-md border-border/40">
              {post.platform === 'yt' ? (
                <Youtube className="h-2.5 w-2.5 text-red-500" />
              ) : post.platform === 'ig' ? (
                <Instagram className="h-2.5 w-2.5 text-purple-600 dark:text-purple-400" />
              ) : (
                <Facebook className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" />
              )}
              {post.platform === 'yt' ? 'YouTube' : post.platform === 'ig' ? 'Instagram' : 'Facebook'}
            </Badge>
            {isStory ? (
              <Badge className="gap-1 border-purple-500/25 bg-purple-500/10 text-[10px] text-purple-600 dark:text-purple-400 rounded-md" variant="outline">Story</Badge>
            ) : isVideo ? (
              <Badge className="gap-1 border-orange-500/25 bg-orange-500/10 text-[10px] text-orange-600 dark:text-orange-400 rounded-md" variant="outline">Reel</Badge>
            ) : (
              <Badge className="gap-1 border-blue-500/25 bg-blue-500/10 text-[10px] text-blue-600 dark:text-blue-400 rounded-md" variant="outline">Imagem</Badge>
            )}
          </div>
          <DialogClose className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground">
            <XIcon className="h-4 w-4" />
          </DialogClose>
        </div>

        {/* Media */}
        {post.imageUrl && (
          <div className="bg-black/95">
            {isVideo ? (
              <video
                src={post.imageUrl}
                controls
                autoPlay
                className="w-full max-h-[60vh] object-contain"
              />
            ) : (
              <img
                src={post.imageUrl}
                alt=""
                className="w-full max-h-[60vh] object-contain"
              />
            )}
          </div>
        )}

        {/* Details */}
        <div className="space-y-3 px-5 py-4">
          {/* Account + Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {post.accountName && (
                <span className="text-xs font-semibold text-foreground">{post.accountName}</span>
              )}
              {post.category && (
                <Badge variant="outline" className="text-[10px] rounded-md border-border/40">{post.category}</Badge>
              )}
            </div>
            <StatusBadge post={post} onShowError={() => setShowError(v => !v)} />
          </div>

          {/* Error */}
          {isFailed && post.errorMessage && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-2.5 py-2">
              <p className="text-[11px] font-medium text-destructive">Retorno da Meta:</p>
              <p className="text-[11px] text-destructive/80 leading-relaxed mt-0.5">{post.errorMessage}</p>
            </div>
          )}

          {/* Title */}
          {post.title && (
            <p className="text-sm font-semibold text-foreground">{post.title}</p>
          )}

          {/* Message */}
          {post.message && (
            <p className="text-xs leading-relaxed text-foreground/90 whitespace-pre-wrap">{post.message}</p>
          )}

          {/* Date + Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-border/30">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground tabular-nums">
              <Clock className="h-3 w-3" />
              {new Date(post.scheduledAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="flex items-center gap-2">
              {post.publishedUrl && (
                <a
                  href={post.publishedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 rounded-lg border border-primary/25 bg-primary/10 px-2.5 py-1.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/20"
                >
                  <ExternalLink className="h-3 w-3" /> Ver post
                </a>
              )}
              {canEdit && onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 text-[11px] rounded-lg"
                  onClick={() => { setShowPreview(false); onEdit(post) }}
                >
                  <Pencil className="h-3 w-3" /> Editar
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}
